import { isoDateTime, z } from "../zod.js";
import { CustomerMergeMoveCounts } from "../sales/merge-schemas.js";

// Module 09 — Sales domain events (spec 09 §6): the typed catalog of what
// this module EMITS today, plus the two names it consumes over the relay.
//
// TWENTY-EIGHT REGISTERED PRODUCER NAMES, each landed WITH its emitter — P3-A's
// four customer events, P3-C-1's twelve quote and order document events,
// P3-C-2's override decision (D-263), P3-D-1's direct-order review
// decision (D-283), P3-D-2's line-cancel delta, `sales.order_line_changed`
// (D-285), P3-D PR-D3's shipment pair, `sales.shipment_shipped` +
// `sales.shipment_delivered` (D-286), P3-E2 PR E2-1's five deal events
// (D-312 ruling 16): `sales.opportunity_created`,
// `sales.opportunity_stage_changed`, `sales.opportunity_won`,
// `sales.opportunity_lost` and `sales.opportunity_reopened`, and P3-E2 PR
// E2-2b's `sales.customer_merged` (D-324 ruling 12), emitted by
// `POST /v1/customers/{id}/merge`, and P3-J J-3's `sales.order_re_promised`
// (D-469 ruling 1), emitted by `POST /v1/orders/{id}/re-promise`. The count
// was twenty-one until E2-1 (21 → 26, the five named above), twenty-six until
// E2-2b (26 → 27, `sales.customer_merged`) and twenty-seven until J-3 (27 →
// 28, `sales.order_re_promised`), and each time it moved here in the same
// edit as the list. §6 prints thirty producer rows; the
// two still absent have no emitter, because an event name declared ahead of
// its emitter is the D-026 stub shape the quality catalog wrote down (D-135,
// restated by every module since): each name joins WITH its emitter, in the
// same change. `sales.contact_channel_verified` stays absent for a reason
// worth stating rather than re-deriving — its §2.4 trigger IS module 17's
// inbound flow, and that flow emits the `messaging.identity_*` pair this
// module CONSUMES instead (D-264). `sales.reliability_scored` has no emitter
// until module 11 gives §3.9's job history to score.
//
// FOUR CONSUMED NAMES, TWO SUBSCRIBERS, STATED RATHER THAN IMPLIED. P3-B part
// 1 added `messaging.identity_verified` / `messaging.identity_revoked` WITH
// `modules/sales/contacts/contact-channel-sync.consumer.ts` — §2.4's
// `is_verified` mirror, both edges. P3-E PR-E1 (D-291 ruling 10) added
// `messaging.message_failed` / `messaging.message_delivery_updated` WITH
// `modules/sales/notifications/delivery-rollup.consumer.ts` — §9.8's delivery
// rollup onto the §2.20 row. Every producer row still carries
// `consumers: []`, and that is a claim about TODAY, not a wish: §6's consumer
// column names modules 13 (analytics), 14 (agent context), 17 (privacy
// cleanup) and 11 (finance), of which 13/14/17 are unbuilt and 11 lands P4.
// This module consumes neither of its own customer events, in any phase. A
// draft quote reprices from the customer's current tier on its next edit
// (§3.1 item 2), which needs no consumer, and a changed limit, terms or hold
// releases no held order by itself, because an order leaves `blocked_*` only
// through the §2.16 credit decision or a recorded prepayment (D-334 ruling
// 3). Declaring a subscription nothing implements fails the
// live-registry parity gate in exactly the direction it exists for (D-175);
// the §6 routing is recorded on each payload's doc block so the absorbing
// slice adds the consumer, not the archaeology.
//
// §6's shared envelope (tenant_id, occurred_at, actor {kind: user|agent|
// system, id}, event id) is added by `OutboxService.append`; each payload
// below carries only its own fields. Every payload is `.parse()`d at its emit
// site in `modules/sales/sales-events.ts`, and `salesProducerEventNames` is
// held against that emitter's string literals BOTH WAYS by
// `test/unit/module-catalog-parity.test.ts`.

const Uuid = z.guid();
const MinorMoney = z.number().int();
const Tier = z.enum(["market", "b2b", "distributor"]);
/** A business date, `YYYY-MM-DD`. */
const BusinessDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u);

/** spec 09 §6 — `sales.customer_created`. §6 routing: 13 (analytics), 14
 * (agent context) — neither exists; no live consumer. Emitter: POST
 * /v1/customers (CustomersService.create). */
export const SalesCustomerCreatedPayload = z.object({
  customer_id: Uuid,
  customer_no: z.string(),
  tier: Tier,
  /** The §2.2 owner assignment made at creation; null when an admin created
   * the customer without taking ownership. */
  owner_user_id: Uuid.nullable(),
});

/** spec 09 §6 — `sales.customer_tier_changed`. §6 routing: 13 (analytics,
 * unbuilt). Module 09 does not consume it: a draft quote reprices from the
 * customer's current tier on its next edit (§3.1 item 2) and a sent quote
 * keeps its pinned prices, so no reaction is owed (D-334 ruling 5). Emitters:
 * PATCH /v1/customers/{id} on a tier transition (§3.1.2 — facility_admin/
 * group_admin only, service-enforced) and POST /v1/customers/{id}/merge when
 * the winner takes the loser's differing tier (§3.13), both through
 * `customer-change-events.ts`. */
export const SalesCustomerTierChangedPayload = z.object({
  customer_id: Uuid,
  old_tier: Tier,
  new_tier: Tier,
});

/** spec 09 §6 — `sales.customer_credit_changed`. §6 routing: 11 finance
 * (consumer lands P4, the cache refresh for the credit checks it answers).
 * Module 09 does not consume it, in any phase (D-334 ruling 3): a held order
 * leaves `blocked_*` only through the §2.16 credit decision or a recorded
 * prepayment, and finance, which may edit a limit, may not release an order
 * past one (D-267). Emitters: PATCH /v1/customers/{id} when
 * credit_limit_minor / credit_terms_days / credit_hold actually change (§7's
 * credit cell: ga/fa/finance) and POST /v1/customers/{id}/merge when the
 * winner takes any of them from the loser (§3.13), both through
 * `customer-change-events.ts`. */
export const SalesCustomerCreditChangedPayload = z.object({
  customer_id: Uuid,
  credit_limit_minor: MinorMoney,
  credit_terms_days: z.number().int(),
  credit_hold: z.boolean(),
});

/** spec 09 §6 — `sales.customer_contact_deleted`. §6 routing: 17 (privacy
 * cleanup of channel identities — module 17 does not exist; the payload IS
 * the contract it will consume), 13. Emitter: PATCH /v1/contacts/{id} with
 * status='archived' (§5 — archiving is this module's contact deletion). */
export const SalesCustomerContactDeletedPayload = z.object({
  customer_id: Uuid,
  contact_id: Uuid,
  /** The channel ids removed with the contact, so 17's cleanup needs no
   * read-back of rows this module may have already hard-deleted. */
  channel_ids: z.array(Uuid),
});

// ===========================================================================
// SLICE P3-C-1 — the quote/order document events (spec 09 §6 rows 6–18, 20–22).
//
// TWELVE NEW NAMES, each landing WITH its emitter in modules/sales, exactly
// as the header above demands. What is STILL absent, and why:
//   * `sales.contact_channel_verified` — its §2.4 trigger is module 17's
//     inbound /start flow, which emits the messaging pair instead. No emitter
//     here, so no name here.
//   * `sales.override_review_decided` — REMOVED from this catalog on
//     2026-09-01 (D-255) because its only emitter, `POST
//     /v1/customer-price-overrides/{id}/decide`, was in no register, and
//     RETURNED on 2026-09-08 (slice P3-C-2, D-263) WITH that route — the
//     block below, the seventeenth name. The removal was the
//     `inventory.level_drift_detected` rule applied to ourselves; the return
//     is the same rule's other half: a name lands with its emitter, in the
//     same change, never ahead of it.
//   * `sales.order_review_decided` — the DIRECT-order pending review (§3.6).
//     LANDED 2026-09-15 (P3-D PR-D1, D-283) WITH its route, `POST
//     /v1/orders/{id}/decide`, beside `POST /v1/orders` — the eighteenth name,
//     at the bottom of this file. It was absent through P3-C for the reason
//     this bullet used to state: a quote-born order never enters
//     `pending_approval` (the review happened on the quote), so nothing could
//     emit it until an order existed that had no quote. There is deliberately
//     NO `sales.order_margin_breach_requested` beside it: a direct order's
//     review request is the `pending_approval` state itself, the
//     `sales_order.review_requested` audit row and the
//     `sales.order_status_changed` (null → `pending_approval`) the create
//     emits — D-231's order rows name only DECIDED events.
//   * `sales.order_line_changed` — the §5 LINE cancel's demand delta (D-235
//     adjudicated that 09 emits it rather than 03 dropping its subscription).
//     LANDED 2026-09-16 (P3-D PR-D2, D-285) WITH its route, `POST
//     /v1/orders/{id}/lines/{lineId}/cancel` — the nineteenth name, at the
//     bottom of this file. Absent through PR-D1 for the plain reason: no
//     route changed a single line's open quantity, so nothing could emit it.
//   * `sales.order_re_promised` — needs module 03's CTP (P3 promise dates are
//     NULL throughout).
//   * `sales.shipment_shipped` / `sales.shipment_delivered` — LANDED
//     2026-09-17 (P3-D PR-D3, D-286) WITH their emitters, `POST
//     /v1/shipments/{id}/ship` and the two delivered doors (`POST
//     /v1/orders/{id}/mark-delivered`, `POST /v1/public/orders/{token}/
//     confirm-received`) — the twentieth and twenty-first names, at the
//     bottom of this file, on migration 0063's tables. Absent through PR-D2
//     for the plain reason: no shipment existed to ship or deliver.
//   * `sales.reliability_scored` — module 11 gives §3.9's job history to score.
//
// CONSUMERS. Every `consumers: []` below is a STATEMENT, not an oversight: 13
// (analytics) and 14 (agents) are unbuilt, 11 lands P4, 12 absorbs the review
// surfaces in P4 (D-231), and modules 03/04 consume `order_confirmed` /
// `order_credit_released` only once planning and MTO WO release exist. The
// notification worker that §3.6 hangs off `sales.order_status_changed` is
// P3-E's; this slice writes the CONFIRMED push inline in the accept
// transaction (§3.5e), which is why the row below still declares no consumer.
// ===========================================================================

/** Per-line breach summary the §4.6 review card renders (§6's
 * `quote_margin_breach_requested` / `quote_review_decided` payloads). The
 * money impact is SERVER-computed (`delta_minor` = the engine's
 * `breachShortfallMinor` × qty): §7 forbids raw cost reaching a surface that
 * lacks `sales.margin.read`, so the card carries the delta, never the cost. */
export const SalesBreachSummary = z.object({
  line_no: z.number().int().positive(),
  product_id: Uuid,
  /** Signed 2-dp percent text; null when the cost basis was unavailable and
   * the guard verdict failed closed (an unprovable margin is not a number). */
  margin_pct: z.string().nullable(),
  /** The guard that applied; null when the line breached for the 0-price
   * reason (§3.2 step 4 — always a breach, with or without a guard row). */
  guard_pct: z.string().nullable(),
  /** Minor units the LINE must gain to clear the guard; null when no finite
   * floor exists (a 100% guard over a positive cost). */
  delta_minor: MinorMoney.nullable(),
});

/** spec 09 §6 — `sales.quote_sent`. §6 routing: 17 (the dispatch itself is
 * INLINE in the send path, not an event subscription — the §6 row says so),
 * 13, 14. Emitter: POST /v1/quotes/{id}/send. */
export const SalesQuoteSentPayload = z.object({
  quote_id: Uuid,
  quote_no: z.string(),
  customer_id: Uuid,
  revision_no: z.number().int().positive(),
  total_minor: MinorMoney,
  currency: z.string().length(3),
  owner_user_id: Uuid,
  origin: z.enum(["manual", "agent", "portal"]),
  off_list_line_count: z.number().int().nonnegative(),
  accept_expires_at: isoDateTime(),
});

/** spec 09 §6 — `sales.quote_revised`. §6 routing: 17 (supersede notice is
 * dispatched inline via the send API, as with quote_sent — not a
 * subscription), 13. Emitter: POST /v1/quotes/{id}/revisions. */
export const SalesQuoteRevisedPayload = z.object({
  quote_id: Uuid,
  old_revision_no: z.number().int().positive(),
  new_revision_no: z.number().int().positive(),
  /** The accept token revoked by this revision; null when the quote had never
   * been sent (a draft edited again issues and revokes nothing). */
  revoked_token_id: Uuid.nullable(),
});

/** spec 09 §6 — `sales.quote_accepted`. §6 routing: 13, 14. Emitters: POST
 * /v1/public/quotes/{token}/accept and POST /v1/quotes/{id}/accept-manual —
 * both run the same §3.5 a–e transaction. */
export const SalesQuoteAcceptedPayload = z.object({
  quote_id: Uuid,
  revision_no: z.number().int().positive(),
  customer_id: Uuid,
  accepted_via: z.enum(["token_link", "manual", "agent"]),
  order_id: Uuid,
});

/** spec 09 §6 — `sales.quote_rejected`. §6 routing: 13, 14 (loss-reason
 * learning). Emitters: POST /v1/public/quotes/{token}/decline (customer) and
 * POST /v1/quotes/{id}/record-rejection (sales). */
export const SalesQuoteRejectedPayload = z.object({
  quote_id: Uuid,
  customer_id: Uuid,
  /**
   * §2.12's lost-reason vocabulary — OMITTED when the customer declined without
   * choosing one (D-225: omission, never a stand-in). §5's decline row allows a
   * decline with no reason ("a required field on a customer-facing decline is a
   * field that produces garbage"), and the door used to emit `"other"` for that
   * case: a value nobody selected, landing in the §6 stream P3-E's loss
   * analysis will read as if a human picked it. `.optional()` and not
   * `.nullable()` — a null here is a second value nobody chose, the same defect
   * in a different costume. The office record-rejection door always carries a
   * real code and is unaffected.
   */
  reason_code: z.enum(["price", "lead_time", "stock", "other"]).optional(),
});

/** spec 09 §6 — `sales.quote_expired`. §6 routing: 13; 14 (follow-up nudge).
 * Emitters: POST /v1/quotes/{id}/expire — the §5 force-expire route, PRINCIPAL
 * actor — and, since P3-E PR-E2 (D-292 ruling 9), the sales engine's expiry
 * sweep, a SECOND emitter of the same name through the same force-expire body
 * with the SYSTEM actor (audit `forced: false`): a `sent` quote whose
 * `valid_until` is before the tenant's business today flips on the first
 * sweep after tenant-local midnight. It adds no name; the payload is
 * identical, so a consumer cannot tell and need not. */
export const SalesQuoteExpiredPayload = z.object({
  quote_id: Uuid,
  customer_id: Uuid,
});

/** spec 09 §6 — `sales.quote_margin_breach_requested`. §6 routing: the §4.6
 * review inbox (in-module, D-231); module 12 absorbs it in P4. Emitter: POST
 * /v1/quotes/{id}/send when the CURRENT revision breaches (§3.4 — re-evaluated
 * regardless of any prior approval). */
export const SalesQuoteMarginBreachRequestedPayload = z.object({
  quote_id: Uuid,
  revision_no: z.number().int().positive(),
  line_summaries: z.array(SalesBreachSummary),
  total_minor: MinorMoney,
  /** Which §3.4 threshold tier the review needs — `facility_admin` when every
   * breach is ≤2pp and no breached line exceeds the value bar, `group_admin`
   * otherwise. Computed SERVER-side so the inbox never re-derives it. */
  required_approver: z.enum(["facility_admin", "group_admin"]),
});

/** spec 09 §6 — `sales.quote_review_decided`. §6 routing: 13, 14; 12
 * (absorption consumer, P4 — D-231). Emitter: POST /v1/quotes/{id}/decide. */
export const SalesQuoteReviewDecidedPayload = z.object({
  quote_id: Uuid,
  revision_no: z.number().int().positive(),
  outcome: z.enum(["approved", "rejected"]),
  decided_by: Uuid,
  note: z.string().nullable(),
  breach_summaries: z.array(SalesBreachSummary),
});

/** spec 09 §6 — `sales.order_confirmed`. §6 routing: 03 planning (MTO demand
 * — DEFERRED while `credit_check_status` is `blocked_*`), 04 production (WO
 * release gate, same rule), 11 finance (P4), the §4.6 review inbox, 13, and
 * the notification worker. Emitter: the §3.5c accept transaction.
 *
 * `credit_check_status` rides the payload BY DESIGN (§6 bolds it): consumers
 * gate on it without reading this module's table. */
export const SalesOrderConfirmedPayload = z.object({
  order_id: Uuid,
  order_no: z.string(),
  customer_id: Uuid,
  lines: z.array(
    z.object({
      line_id: Uuid,
      product_id: Uuid,
      qty: z.string(),
      uom_id: Uuid,
      fulfillment_mode: z.enum(["stock", "offcut_combo", "production", "mixed"]),
    }),
  ),
  total_minor: MinorMoney,
  currency: z.string().length(3),
  /** The latest dated line's promise date, or NULL when no line carries one (D-349 ruling 1). */
  promise_date: z.string().nullable(),
  fulfilling_facility_id: Uuid,
  credit_check_status: z.enum(["passed", "blocked_credit", "blocked_prepay", "override_approved"]),
});

/** spec 09 §6 — `sales.order_credit_released`. §6 routing: 03 (start deferred
 * MTO demand), 04 (WO release unblocked), §4.6 (close card), 13. Emitter:
 * POST /v1/orders/{id}/credit-decision. */
export const SalesOrderCreditReleasedPayload = z.object({
  order_id: Uuid,
  order_no: z.string(),
  customer_id: Uuid,
  prior_status: z.enum(["blocked_credit", "blocked_prepay"]),
  new_status: z.enum(["override_approved", "passed"]),
  released_via: z.enum(["approval", "prepayment"]),
});

/** spec 09 §6 — `sales.order_status_changed`. §6 routing: the notification
 * worker (09, P3-E), 17 (`/status` card cache invalidation), 13, 12 (P4).
 * Emitters this slice: entry to `confirmed` (§3.5) and to `cancelled` (§5
 * order cancel). The 04-driven transitions (in_production/ready) and the
 * shipment-driven ones land with the slices that consume those events. Since
 * P3-E PR-E2 (D-292 ruling 10) the sales engine's auto-confirm emits it too —
 * `shipped → delivered` through the one transition body, SYSTEM actor — beside
 * one `sales.shipment_delivered` (`confirmed_by: auto`) per shipment. */
export const SalesOrderStatusChangedPayload = z.object({
  order_id: Uuid,
  order_no: z.string(),
  customer_id: Uuid,
  /** Null on the first transition INTO the document's initial state. */
  old_status: z.string().nullable(),
  new_status: z.string(),
  public_token: Uuid,
});

/** spec 09 §6 — `sales.order_fulfillment_degraded`. §6 routing: the owner
 * manager's inbox + the §4.6 review feed (money impact = the discount delta);
 * 12 exception from P4. Emitter: §3.5a, when a named stock unit is gone and no
 * same-or-better replacement combo exists (M09-S06). */
export const SalesOrderFulfillmentDegradedPayload = z.object({
  order_id: Uuid,
  line_id: Uuid,
  /** The §2.14 plan as the customer's accepted revision held it. */
  planned_plan: z.array(z.record(z.string(), z.unknown())),
  actual_mode: z.enum(["stock", "offcut_combo", "production", "mixed"]),
  /** The money the degradation costs: the off-cut discount the line keeps
   * (price stays as quoted — §9.2: the discount was contractual) expressed
   * against the line total. Null when no discount class applied. */
  discount_delta_minor: MinorMoney.nullable(),
});

/** spec 09 §6 — `sales.order_cancelled`. §6 routing: 03, 06 (release confirm),
 * 11 (P4), 13. Emitter: POST /v1/orders/{id}/cancel. */
export const SalesOrderCancelledPayload = z.object({
  order_id: Uuid,
  reason: z.string(),
  released_reservation_ids: z.array(Uuid),
});

// ===========================================================================
// SLICE P3-C-2 — the §2.9 override review's decided event (spec 09 §6 row
// `sales.override_review_decided`; D-263, 2026-09-08). ONE new name, landing
// WITH its emitter: `POST /v1/customer-price-overrides/{id}/decide`, the
// D-231 in-module decision that `register.ts` now mounts. A REVOKE emits
// nothing — it is an administrative withdrawal, audited, not a review
// decision; and a proposal that lands `active` without breaching emits
// nothing either, because no review was opened to decide.
// ===========================================================================

/** spec 09 §6 — `sales.override_review_decided`. §6 routing: 13; 12
 * (absorption consumer, P4 — D-231). Emitter: POST
 * /v1/customer-price-overrides/{id}/decide. The row itself carries no
 * decision column (§2.9's `approval_request_id` is module 12's, NULL through
 * P3), so this payload IS the decision record: `note` is the decider's
 * mandatory rejection note (null on approval), which on rejection also
 * overwrites the row's `reason` (§5). */
export const SalesOverrideReviewDecidedPayload = z.object({
  override_id: Uuid,
  customer_id: Uuid,
  product_id: Uuid,
  outcome: z.enum(["approved", "rejected"]),
  decided_by: Uuid,
  note: z.string().nullable(),
});

// ===========================================================================
// SLICE P3-D-1 (D-283) — the DIRECT ORDER's review decision, landing WITH its
// emitter (`POST /v1/orders/{id}/decide`) and its producer (`POST /v1/orders`,
// which mints the `pending_approval` order the decision is about). The
// `sales.quote_review_decided` twin with `order_no` in place of the revision
// number: an order has no revisions, and the number is what every §4.6 card,
// audit row and notification names it by.
// ===========================================================================

/** spec 09 §6 — `sales.order_review_decided`. §6 routing: 13, 14; 12
 * (absorption consumer, P4 — D-231). Emitter: POST /v1/orders/{id}/decide.
 * §2.16 carries no decision column (`credit_approval_request_id` is module
 * 12's and is about credit), so this payload IS the decision record: `note`
 * is the decider's mandatory rejection note (null on approval), which on
 * rejection is also written verbatim as the order's `cancelled_reason`
 * (D-267). `breach_summaries` are the lines that put the order in review,
 * from the order line's own stored facts (§2.17). */
export const SalesOrderReviewDecidedPayload = z.object({
  order_id: Uuid,
  order_no: z.string(),
  outcome: z.enum(["approved", "rejected"]),
  decided_by: Uuid,
  note: z.string().nullable(),
  breach_summaries: z.array(SalesBreachSummary),
});

// ===========================================================================
// SLICE P3-D PR-D2 (D-285) — the LINE cancel's demand delta, landing WITH its
// emitter (`POST /v1/orders/{id}/lines/{lineId}/cancel`). D-235 adjudicated
// the name's existence in the P3 planning read — module 03 consumed it and
// module 09 never emitted it — and ruled that 09 emits it rather than 03
// dropping a subscription the demand pool genuinely needs. The payload below
// is D-235's printed shape VERBATIM; widening it (a line total, a currency)
// is a countersigned ruling of its own, not a slice's drift.
// ===========================================================================

/** spec 09 §6 — `sales.order_line_changed`. §6 routing: 03 planning (line-level
 * demand delta, debounced refresh — D-235), 13. Emitter:
 * POST /v1/orders/{id}/lines/{lineId}/cancel (PR-D2, D-285). Quantities are
 * decimal TEXT (D-041), in the SAME scale `qty_ordered::text` answers in
 * (orders.repo.ts's line read) — the `SalesOrderConfirmedPayload.lines[].qty`
 * rule above. `old_qty_open` is that stored text verbatim: both shipped arms
 * of the route refuse, so nothing it cancels has shipped and no arithmetic
 * is done on a quantity. `reason` is an enum of ONE: §9.18 rules partial-line
 * cancellation out of v1, so the future qty-amendment surface ADDS a member
 * WITH its emitter rather than minting a name (D-235). No money:
 * `sales.order_cancelled` carries none either, and 13 reads the value back
 * from 09's order surface (13 §6). The LAST non-cancelled line does NOT emit
 * this — it runs the whole-order cancel, which emits the order pair (D-285
 * ruling 5: one press, one fact; both would hand 03/06 the same hold ids
 * twice). */
export const SalesOrderLineChangedPayload = z.object({
  order_id: Uuid,
  line_id: Uuid,
  product_id: Uuid,
  uom_id: Uuid,
  old_qty_open: z.string(),
  new_qty_open: z.string(),
  /** `line_cancelled` from the §5 line cancel; `promise_dated` from module
   * 09's stamp of a committed promise (P3-J J-2, D-465 ruling 4), with
   * `old_qty_open` equal to `new_qty_open` and no released holds, so module
   * 03's apply only arms the demand debounce, after the stamp. */
  reason: z.enum(["line_cancelled", "promise_dated"]),
  released_reservation_ids: z.array(Uuid),
});

// ===========================================================================
// SLICE P3-D PR-D3 (D-286) — the SHIPMENT pair, landing WITH their emitters
// on migration 0063's tables: `sales.shipment_shipped` from `POST
// /v1/shipments/{id}/ship`, and `sales.shipment_delivered` from the two
// delivered doors (the office's `POST /v1/orders/{id}/mark-delivered` and the
// customer's `POST /v1/public/orders/{token}/confirm-received`). Both payloads
// are spec 09 §6's printed shapes VERBATIM; widening either (a delivery note
// id, a carrier) is a countersigned ruling of its own, not a slice's drift.
// Nothing is emitted at PICK: the pick is a module-06 `shipment` movement, and
// `inventory.movement_recorded` (+ `reservation_created` for a remainder) IS
// the event; a second name for the same fact would hand every consumer the
// same movement twice.
// ===========================================================================

/** The §2.18 `delivered_by` set (`DeliveredBy` in sales/schemas.ts, restated
 * here so the events catalog imports nothing from the DTO layer, as every
 * catalog in this directory does). `auto` has had its producer since P3-E
 * PR-E2 (D-292 ruling 10): the sales engine's auto-confirm, which delivers a
 * `shipped` order `delivery_auto_confirm_days` after `shipped_at`. */
const ShipmentDeliveredBy = z.enum(["customer", "carrier", "auto"]);

/** spec 09 §6 — `sales.shipment_shipped`. §6 routing: 11 finance (invoice
 * creation — THE PAYLOAD IS THE INVOICE CONTRACT; the consumer is declared and
 * honestly absent until P4, D-198), 03 (promise verdicts), 06 (the genealogy
 * terminal is already posted — the movement rode the pick, so 06 consumes
 * nothing here; spec 06 §6 records the deliberate non-subscription, the
 * `production.material_issued` standing), 13. Emitter: POST
 * /v1/shipments/{id}/ship, with the PRINCIPAL actor (a storekeeper pressed
 * Ship). `lines[].qty_picked` is the stored 3-dp text (D-041, the line's UoM);
 * `unit_price_minor` is the STORED order-line price — never re-resolved
 * (§9.3) — and `stock_unit_id` the PARENT unit (no cut child is minted, D-208
 * pt 5; a partially shipped remnant stays on the same row). `currency` is the
 * order's. Emitted ONCE per shipment; a partial shipment emits it too, and
 * the order's own `sales.order_status_changed` follows only when the shipment
 * leaves every non-cancelled line `shipped`. */
export const SalesShipmentShippedPayload = z.object({
  shipment_id: Uuid,
  shipment_no: z.string(),
  order_id: Uuid,
  customer_id: Uuid,
  facility_id: Uuid,
  lines: z.array(
    z.object({
      sales_order_line_id: Uuid,
      stock_unit_id: Uuid,
      lot_id: Uuid,
      qty_picked: z.string(),
      uom_id: Uuid,
      unit_price_minor: MinorMoney,
    }),
  ),
  currency: z.string().length(3),
});

/** spec 09 §6 — `sales.shipment_delivered`. §6 routing: 11 (the
 * payment-terms clock — consumer lands P4), 13. Emitters: the office's POST
 * /v1/orders/{id}/mark-delivered (`confirmed_by: carrier`, PRINCIPAL actor),
 * the customer's POST /v1/public/orders/{token}/confirm-received
 * (`confirmed_by: customer`, SYSTEM actor — the customer is not a principal
 * of this tenant, the accept door's rule), and — since P3-E PR-E2 (D-292
 * ruling 10) — the sales engine's auto-confirm (`confirmed_by: auto`, SYSTEM
 * actor), which sends the customer no push. Delivery is decided at ORDER grain
 * and emitted ONE PER SHIPMENT (D-286 ruling 14): every `shipped` shipment of
 * the order is stamped in the same transaction and each gets its own row, so
 * a consumer keyed on `shipment_id` needs no order read. `confirmed_by`
 * matches the row's `delivered_by`. */
export const SalesShipmentDeliveredPayload = z.object({
  shipment_id: Uuid,
  order_id: Uuid,
  delivered_at: isoDateTime(),
  confirmed_by: ShipmentDeliveredBy,
});

// --- P3-E2 PR E2-1 (D-312; migration 0073): the deal events ---------------
//
// Five names, each landed WITH its emitter (D-312 ruling 16; the D-198
// pairing rule): the create, the move, the win (a person's, and the §3.5
// step 7a win by the customer's acceptance with the system actor), the loss
// and the reopen. §6 routes each to 13 (analytics) and 14 (agent context),
// both unbuilt, so every row carries `consumers: []`. A deal's PATCH and the
// quote link emit nothing, and a stage's tuning emits nothing. The payloads
// carry the deal's value, which the audit rows deliberately do not (D-312
// ruling 17): an event reaches only in-process consumers, never a reader of
// the chain.

const OpportunityMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
const OpportunityCurrency = z.string().regex(/^[A-Z]{3}$/);
const OpportunityLostReasonCode = z.enum(["price", "lead_time", "stock", "other"]);
/** NULL when nobody estimated the value — never read as zero (§2.25). */
const OpportunityValueMinor = MinorMoney.min(0).nullable();

/** spec 09 §6 — `sales.opportunity_created`. §6 routing: 13, 14 (declared
 * absent, D-198). Emitter: POST /v1/opportunities, principal actor.
 * `source_conversation_id` is present only when the deal names one. */
export const SalesOpportunityCreatedPayload = z.object({
  opportunity_id: Uuid,
  opportunity_no: z.string(),
  customer_id: Uuid,
  owner_user_id: Uuid,
  stage_id: Uuid,
  stage_code: z.string(),
  currency: OpportunityCurrency,
  expected_value_minor: OpportunityValueMinor,
  expected_close_month: OpportunityMonth,
  source_conversation_id: Uuid.optional(),
});

/** spec 09 §6 — `sales.opportunity_stage_changed`. §6 routing: 13, 14.
 * Emitter: POST /v1/opportunities/{id}/move — never on a move to the deal's
 * own stage, which writes nothing. `probability_pct` is the destination
 * stage's current probability. */
export const SalesOpportunityStageChangedPayload = z.object({
  opportunity_id: Uuid,
  customer_id: Uuid,
  from_stage_id: Uuid,
  to_stage_id: Uuid,
  from_stage_code: z.string(),
  to_stage_code: z.string(),
  probability_pct: z.number().int().min(0).max(100),
});

/** spec 09 §6 — `sales.opportunity_won`. §6 routing: 13, 14. Emitters: POST
 * /v1/opportunities/{id}/win (principal actor, `won_via: manual`) and §3.5
 * step 7a inside the acceptance transaction (system actor, `won_via:
 * quote_accepted`, the sixth accept-door event when the quote names a deal).
 * `won_quote_id` / `won_order_id` are present only when the win names them;
 * `prior_status` is `lost` when the customer's acceptance wins a lost deal. */
export const SalesOpportunityWonPayload = z.object({
  opportunity_id: Uuid,
  customer_id: Uuid,
  owner_user_id: Uuid,
  won_via: z.enum(["manual", "quote_accepted"]),
  won_quote_id: Uuid.optional(),
  won_order_id: Uuid.optional(),
  prior_status: z.enum(["open", "lost"]),
  stage_code: z.string(),
  currency: OpportunityCurrency,
  expected_value_minor: OpportunityValueMinor,
});

/** spec 09 §6 — `sales.opportunity_lost`. §6 routing: 13, 14 (loss-reason
 * learning reads §2.12's vocabulary, the one a rejected quote carries).
 * Emitter: POST /v1/opportunities/{id}/lose. The note never rides the event. */
export const SalesOpportunityLostPayload = z.object({
  opportunity_id: Uuid,
  customer_id: Uuid,
  owner_user_id: Uuid,
  reason_code: OpportunityLostReasonCode,
  stage_code: z.string(),
  currency: OpportunityCurrency,
  expected_value_minor: OpportunityValueMinor,
});

/** spec 09 §6 — `sales.opportunity_reopened`. §6 routing: 13, 14. Emitter:
 * POST /v1/opportunities/{id}/reopen. `stage_id` is the stage the deal
 * returns to, the one it closed from. */
export const SalesOpportunityReopenedPayload = z.object({
  opportunity_id: Uuid,
  customer_id: Uuid,
  prior_status: z.enum(["won", "lost"]),
  stage_id: Uuid,
});

/** spec 09 §6 — `sales.customer_merged` (P3-E2 PR E2-2b, D-324 ruling 12).
 * §6 routing: 13, 14 (declared absent, D-198). Emitter: POST
 * /v1/customers/{id}/merge, PRINCIPAL actor, inside the merge's transaction.
 * `moved` counts the rows re-parented or copied onto the winner per table, the
 * shape the merge answers and `customer_merges.moved` records
 * (`CustomerMergeMoveCounts`, spec 09 §2.33). No profile value rides it: the
 * winner's overwritten values stay in `customer_merges.winner_before`, which
 * no reader is served. Module 17's conversation re-point inside the merge
 * emits nothing of its own (D-324 ruling 11), and the restore emits nothing. */
export const SalesCustomerMergedPayload = z.object({
  merge_id: Uuid,
  winner_customer_id: Uuid,
  loser_customer_id: Uuid,
  moved: CustomerMergeMoveCounts,
});

/** spec 09 §6 — `sales.order_re_promised` (P3-J J-3, D-469 ruling 1). §6
 * routing: 13, 12 (declared absent). Emitter: POST /v1/orders/{id}/re-promise,
 * PRINCIPAL actor, inside module 03's commit transaction after the line is
 * stamped. `old_promise_date` is the line's date before the press (null for
 * an undated line); `notified_customer` says whether an `order_re_promised`
 * message was queued. No money rides it. */
export const SalesOrderRePromisedPayload = z.object({
  order_id: Uuid,
  sales_order_line_id: Uuid,
  promise_id: Uuid,
  old_promise_date: BusinessDate.nullable(),
  new_promise_date: BusinessDate,
  notified_customer: z.boolean(),
});

export const salesProducerEvents = {
  "sales.customer_created": {
    payload: SalesCustomerCreatedPayload,
    consumers: [],
  },
  "sales.customer_tier_changed": {
    payload: SalesCustomerTierChangedPayload,
    consumers: [],
  },
  "sales.customer_credit_changed": {
    payload: SalesCustomerCreditChangedPayload,
    consumers: [],
  },
  "sales.customer_contact_deleted": {
    payload: SalesCustomerContactDeletedPayload,
    // Module 17 EXISTS now (P3-B part 1): its
    // seams/customer-contact-deleted.consumer.ts revokes the archived
    // contact's identities off this payload's channel_ids — the subscriber
    // this row's doc block always promised, landed WITH its declaration.
    consumers: ["17"],
  },
  // --- P3-C-1: the quote/order document events. Every `consumers: []` is the
  // statement the block above makes — no live subscriber exists for it TODAY,
  // and the slice that builds one adds itself here rather than the archaeology.
  "sales.quote_sent": { payload: SalesQuoteSentPayload, consumers: [] },
  "sales.quote_revised": { payload: SalesQuoteRevisedPayload, consumers: [] },
  "sales.quote_accepted": { payload: SalesQuoteAcceptedPayload, consumers: [] },
  "sales.quote_rejected": { payload: SalesQuoteRejectedPayload, consumers: [] },
  "sales.quote_expired": { payload: SalesQuoteExpiredPayload, consumers: [] },
  "sales.quote_margin_breach_requested": {
    payload: SalesQuoteMarginBreachRequestedPayload,
    consumers: [],
  },
  "sales.quote_review_decided": { payload: SalesQuoteReviewDecidedPayload, consumers: [] },
  "sales.order_confirmed": { payload: SalesOrderConfirmedPayload, consumers: ["03"] },
  "sales.order_credit_released": { payload: SalesOrderCreditReleasedPayload, consumers: ["03"] },
  "sales.order_status_changed": { payload: SalesOrderStatusChangedPayload, consumers: [] },
  "sales.order_fulfillment_degraded": {
    payload: SalesOrderFulfillmentDegradedPayload,
    consumers: [],
  },
  "sales.order_cancelled": { payload: SalesOrderCancelledPayload, consumers: ["03"] },
  // --- P3-C-2 (D-263): the override review's decision. `consumers: []` is the
  // same statement as every row above — 13 is unbuilt and 12 absorbs the
  // surface in P4 (D-231); the §4.6 inbox reads the row's status, not the
  // relay.
  "sales.override_review_decided": {
    payload: SalesOverrideReviewDecidedPayload,
    consumers: [],
  },
  // --- P3-D-1 (D-283): the direct order's review decision. `consumers: []` is
  // the same statement as every row above — 13/14 are unbuilt and 12 absorbs
  // the surface in P4 (D-231); the §4.6 inbox reads the order's status, not
  // the relay.
  "sales.order_review_decided": {
    payload: SalesOrderReviewDecidedPayload,
    consumers: [],
  },
  // --- P3-D PR-D2 (D-285): the line cancel's demand delta. Module 03's
  // demand debounce consumes it (spec 03 §3.1, D-349 ruling 14); 13 is unbuilt.
  "sales.order_line_changed": {
    payload: SalesOrderLineChangedPayload,
    consumers: ["03"],
  },
  // --- P3-D PR-D3 (D-286): the shipment pair. Module 03's demand debounce
  // consumes `shipment_shipped` (D-349 ruling 14); 11 lands P4 and 13 is
  // unbuilt, and 06 consumes nothing because the movement rode the pick
  // synchronously (spec 06 §6).
  "sales.shipment_shipped": {
    payload: SalesShipmentShippedPayload,
    consumers: ["03"],
  },
  "sales.shipment_delivered": {
    payload: SalesShipmentDeliveredPayload,
    consumers: [],
  },
  // --- P3-E2 PR E2-1 (D-312 ruling 16): the five deal events. `consumers: []`
  // is the same statement as every row above — 13 and 14 are unbuilt and
  // declared absent (D-198); the slice that builds one adds itself here WITH
  // its consumer.
  "sales.opportunity_created": {
    payload: SalesOpportunityCreatedPayload,
    consumers: [],
  },
  "sales.opportunity_stage_changed": {
    payload: SalesOpportunityStageChangedPayload,
    consumers: [],
  },
  "sales.opportunity_won": {
    payload: SalesOpportunityWonPayload,
    consumers: [],
  },
  "sales.opportunity_lost": {
    payload: SalesOpportunityLostPayload,
    consumers: [],
  },
  "sales.opportunity_reopened": {
    payload: SalesOpportunityReopenedPayload,
    consumers: [],
  },
  // --- P3-E2 PR E2-2b (D-324 ruling 12): the merge. `consumers: []` is the
  // same statement as every row above — 13 and 14 are unbuilt and declared
  // absent (D-198); the slice that builds one adds itself here WITH its
  // consumer.
  "sales.customer_merged": {
    payload: SalesCustomerMergedPayload,
    consumers: [],
  },
  // --- P3-J J-3 (D-469 ruling 1): the re-promise. 13 and 12 are unbuilt and
  // declared absent (D-198), so `consumers: []` as above.
  "sales.order_re_promised": {
    payload: SalesOrderRePromisedPayload,
    consumers: [],
  },
} as const;

export type SalesEventName = keyof typeof salesProducerEvents;

export const salesProducerEventNames = Object.keys(salesProducerEvents) as SalesEventName[];

/**
 * Events module 09 CONSUMES over the relay (spec 09 §6 "Consumed by this
 * module"). P3-A shipped this EMPTY, deliberately — a consumed-event name
 * declared ahead of its subscriber is the identical defect as a producer name
 * declared ahead of its emitter, pointed the other way (the module-07 rule,
 * verbatim).
 *
 * P3-B part 1 (module 17 core) adds the TWO names of the
 * `contact_channels.is_verified` mirror, WITH their subscriber
 * (modules/sales/contacts/contact-channel-sync.consumer.ts, this slice):
 * §2.4's `is_verified` flips true on `messaging.identity_verified` and back
 * on `messaging.identity_revoked` for customer-contact identities — one flag,
 * both edges, or the mirror lies in one direction.
 *
 * P3-E PR-E1 (D-291 ruling 10) adds TWO more WITH their subscriber, the §9.8
 * delivery rollup (modules/sales/notifications/delivery-rollup.consumer.ts):
 * `messaging.message_failed` and `messaging.message_delivery_updated`. Their
 * spec 17 §6 doc blocks named this rollup as landing "with the notification
 * engine's slice"; this is that slice.
 *
 * Of the §6 names still absent: most have producers that do not exist
 * (03/08/10/11/12 unbuilt; 06's `inventory.reservation_released` would drive
 * ORDER status automation — P3-C's tables); `sales.contact_channel_verified` remains emitter-less (its §2.4
 * trigger IS the module-17 flow, which emits the messaging pair instead).
 * The one §6 subscription P3-A COULD honestly make —
 * `catalogue.cost_run_released` (01 — margin-guard cost refresh) — is not
 * made, for the module-07 `unit_remeasured` reason restated: the §3.2 engine
 * reads standard cost LIVE from module 01's service at resolve time, so there
 * is no cached number for a subscriber to refresh and the name would ride the
 * relay to do nothing. The name lands WITH a worker, if a later slice caches
 * cost and finds a reader that needs one.
 *
 * Module 04's production events (`work_order_released`,
 * `operation_started`, `work_order_completed`) are NOT consumed, by ruling
 * (P3-D PR-D6, D-290 ruling 1): the order line's `in_production` status is a
 * GUARD (the order cancel, the line cancel, the pick and the forced ship read
 * it), and a guard cannot ride an at-least-once, unordered relay that parks
 * failures. Module 04 calls module 09's peg port SYNCHRONOUSLY inside its own
 * work-order transactions instead, so the line and the order move in the same
 * commit as the work order.
 */
export const salesConsumedEventNames = [
  "messaging.identity_verified",
  "messaging.identity_revoked",
  // P3-E PR-E1 (D-291 ruling 10) — the §9.8 delivery rollup, 2 → 4 names:
  // `messaging.message_failed` flips the §2.20 row it names by
  // `notification_id` to `failed` (with its reason, in the same statement,
  // 0058's CHECK), and `messaging.message_delivery_updated` advances the row
  // found by `outbound_message_id` from `sent` to `delivered`. Monotonic and
  // idempotent through `sales_processed_events`; the subscriber is
  // `modules/sales/notifications/delivery-rollup.consumer.ts`. The consumers
  // arrays on both names in events/messaging.ts and the live-registry pin in
  // apps/api/test/integration/outbox-registry-parity.int.test.ts (19 → 21)
  // moved in the same change.
  "messaging.message_failed",
  "messaging.message_delivery_updated",
  // P3-H-3 (D-391 ruling 2) — the reservation leg of the system `→ ready`,
  // 4 → 5 names: a `sales_order_line` hold re-derives its line in module
  // 09's own transaction, header first, because module 06's reservation
  // transaction cannot take the sales header after its own rows. Forward-only
  // and monotone, so the unordered relay is safe; deduped through
  // `sales_processed_events`; the subscriber is
  // `modules/sales/orders/ready-from-reservation.consumer.ts`. The live
  // registry pin in apps/api/test/integration/outbox-registry-parity.int.test.ts
  // (30 → 31) and the consumers row in events/inventory.ts moved in the same
  // change.
  "inventory.reservation_created",
  // P3-J J-2 (D-465 rulings 3 and 4, D-468 ruling 2) — module 03's promises,
  // 5 → 8 names: `planning.promise_committed` stamps the line, the header and
  // the mirror and emits `sales.order_line_changed` with `promise_dated`;
  // `planning.promise_withdrawn` clears the mirror and the line keeps its
  // date; `planning.order_promises_settled` writes the deferred Confirmed
  // message. Deduped through `sales_processed_events`; the subscribers are
  // `modules/sales/planning-consumers.ts`. The live registry pin in
  // apps/api/test/integration/outbox-registry-parity.int.test.ts (41 → 44)
  // and the "09" consumer rows in events/planning.ts moved in the same change.
  "planning.promise_committed",
  "planning.promise_withdrawn",
  "planning.order_promises_settled",
  // P3-J J-3 (D-466, D-467, D-468 ruling 2) — the watch's four, 8 → 12 names:
  // each keeps the order line's mirror (`promise_status`, its risk reason and
  // projected date) from module 03's state now, for spec 09 §4.7's flags and
  // the at-risk filter, and messages no customer. Deduped through
  // `sales_processed_events`; the subscribers are
  // `modules/sales/planning-consumers.ts`. The live registry pin in
  // apps/api/test/integration/outbox-registry-parity.int.test.ts (44 → 50,
  // with module 03's two watch triggers) and the "09" consumer rows in
  // events/planning.ts moved in the same change.
  "planning.promise_at_risk",
  "planning.promise_recovered",
  "planning.promise_broken",
  "planning.promise_kept",
] as const;
